import React from 'react';
import classnames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { CustomSwitch, RadioWithLabel } from 'components';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { OutlinedInfoIcon } from 'assets/icons';
import { selectUserDetails } from 'containers/app/appSlice';
import { Discount } from 'containers/settings/invoiceSettingsSlice';
import { DISCOUNT_CALCULATION_OPTIONS, DiscountCalculationType } from 'containers/settings/constants';
import { useAppSelector } from 'hooks';
import { useState } from 'react';
import { FirebaseStorage } from 'services/firebase';
import { VoidFn } from 'types/baseTypes';
import DiscountCalculationFeatureAnnouncement, {
  DISCOUNT_CALCULATION_ANNOUNCEMENT_FIELD_INVOICE_SETTINGS
} from './DiscountCalculationFeatureAnnouncement';

interface DiscountSectionProps {
  discount: Discount;
  toggleDiscount: VoidFn;
  onTaxTimingChange: (taxTiming: DiscountCalculationType) => void;
  isEditDisabled?: boolean;
}

const DiscountSection: React.FC<DiscountSectionProps> = ({
  discount,
  toggleDiscount,
  onTaxTimingChange,
  isEditDisabled = false
}) => {
  const merchantId = useAppSelector(selectUserDetails)?.id;
  const [isSettingsAnnouncementDismissed, setIsSettingsAnnouncementDismissed] = useState(false);
  const selectedTaxTiming = discount?.taxTiming ?? DiscountCalculationType.BEFORE_TAX;

  const handleTaxTimingChange = (taxTiming: DiscountCalculationType) => {
    if (taxTiming === selectedTaxTiming) return;

    setIsSettingsAnnouncementDismissed(true);
    if (merchantId) {
      void FirebaseStorage.updateMerchantCollectionFieldValue({
        merchantId,
        collectionFieldName: DISCOUNT_CALCULATION_ANNOUNCEMENT_FIELD_INVOICE_SETTINGS,
        value: true
      }).catch(() => undefined);
    }

    onTaxTimingChange(taxTiming);
  };

  const [firstCalculationOption, ...restCalculationOptions] = DISCOUNT_CALCULATION_OPTIONS;

  const renderCalculationRow = (option: typeof DISCOUNT_CALCULATION_OPTIONS[number]) => (
    <div className="flex items-center gap-2">
      <RadioWithLabel
        isSelected={selectedTaxTiming === option.id}
        label={option.label}
        onClick={() => handleTaxTimingChange(option.id)}
        containerClassname="text-sbase"
      />
      <TooltipWrapper
        tooltip={option.tooltip}
        position="top"
        id={`discount-calculation-${option.id}-tooltip`}
        allowMultilineTooltip>
        <OutlinedInfoIcon
          className="h-4 w-4 cursor-pointer text-tertiaryText"
          aria-label={`${option.label} info`}
          role="img"
          tabIndex={0}
        />
      </TooltipWrapper>
    </div>
  );

  return (
    <div
      className={twMerge(
        'mt-[30px]',
        classnames({
          'pointer-events-none opacity-50': isEditDisabled
        })
      )}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-xl font-bold text-primaryText">Discount</div>
          <div className="mt-2 text-17px font-normal text-primaryText">
            Give customers discounts by % or amount on the overall invoice.
          </div>
        </div>
        <div className="ml-6">
          <CustomSwitch enabled={discount?.enabled} toggleSwitch={toggleDiscount} id="discount-switch" />
        </div>
      </div>

      {discount?.enabled && (
        <div className="mt-6">
          <div className="text-17px font-semibold text-primaryText">Default discount calculation</div>
          <div className="mt-2 text-sbase font-normal text-primaryText">
            This controls how discounts are calculated by default on new invoices. Team members can override this on
            individual transactions.
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <div className="flex flex-col gap-3 md:block">
              <div className="relative w-full max-w-full md:w-fit md:shrink-0">
                {renderCalculationRow(firstCalculationOption)}
                {!isSettingsAnnouncementDismissed && (
                  <DiscountCalculationFeatureAnnouncement
                    dismissFirestoreField={DISCOUNT_CALCULATION_ANNOUNCEMENT_FIELD_INVOICE_SETTINGS}
                    className="md:ml-5"
                  />
                )}
              </div>
            </div>
            {restCalculationOptions.map(option => (
              <div key={option.id} className="w-fit max-w-full shrink-0">
                {renderCalculationRow(option)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountSection;
